<template>
  <div class="xhs-discovery">
    <div class="page-head">
      <el-button :icon="Refresh" :loading="loadingAccounts" @click="loadAccounts">刷新</el-button>
    </div>

    <el-card class="filter-card" shadow="never">
      <el-form @submit.prevent="runSearch(1, false)">
        <el-row :gutter="12">
          <el-col :span="6">
            <div class="field-label">搜索账号</div>
            <el-select v-model="accountId" placeholder="选择 PC 账号" filterable style="width: 100%">
              <el-option
                v-for="a in pcAccounts"
                :key="a.account_id"
                :label="`${a.display_name || a.account_id} (${a.account_id})`"
                :value="a.account_id"
              />
            </el-select>
          </el-col>
          <el-col :span="8">
            <div class="field-label">关键词</div>
            <el-input v-model="keyword" placeholder="低卡早餐、通勤穿搭" @keyup.enter="runSearch(1, false)">
              <template #append>
                <el-button type="primary" :icon="Search" :loading="searching" @click="runSearch(1, false)">搜索</el-button>
              </template>
            </el-input>
          </el-col>
          <el-col :span="3">
            <div class="field-label">排序</div>
            <el-select v-model="filters.sort_type_choice" style="width: 100%">
              <el-option v-for="o in sortOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </el-col>
          <el-col :span="3">
            <div class="field-label">类型</div>
            <el-select v-model="filters.note_type" style="width: 100%">
              <el-option v-for="o in typeOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </el-col>
          <el-col :span="2">
            <div class="field-label">时间</div>
            <el-select v-model="filters.note_time" style="width: 100%">
              <el-option v-for="o in timeOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </el-col>
          <el-col :span="2">
            <div class="field-label">范围</div>
            <el-select v-model="filters.note_range" style="width: 100%">
              <el-option v-for="o in rangeOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </el-col>
        </el-row>
        <el-row :gutter="12" style="margin-top: 12px" align="middle">
          <el-col :span="14">
            <div class="field-label">笔记 URL</div>
            <el-input v-model="noteUrl" placeholder="https://www.xiaohongshu.com/explore/..." />
          </el-col>
          <el-col :span="4">
            <el-button :loading="fetchingUrl" :disabled="!pcAccounts.length" @click="fetchByUrl">URL 直查</el-button>
          </el-col>
        </el-row>
      </el-form>

      <el-alert v-if="error" :title="error" type="error" show-icon closable style="margin-top: 12px" @close="error = ''" />
      <el-empty v-if="!loadingAccounts && !pcAccounts.length" description="还没有可用的 PC 账号">
        <el-button type="primary" @click="$router.push('/publish')">去绑定账号</el-button>
      </el-empty>
    </el-card>

    <el-card shadow="never" class="result-card">
      <template #header>
        <span>{{ searchedKeyword ? `"${searchedKeyword}" 的搜索结果` : '搜索结果' }}</span>
        <el-tag size="small" style="margin-left: 8px">{{ notes.length }} 篇</el-tag>
      </template>

      <el-empty v-if="!notes.length" :description="searchedKeyword ? '这次搜索没有返回笔记' : '输入关键词后，搜索结果会显示在这里'" />
      <el-row v-else :gutter="16">
        <el-col v-for="note in notes" :key="note.note_id + note.title" :xs="12" :sm="8" :md="6" :lg="4">
          <el-card class="note-card" shadow="hover" @click="openDetail(note)">
            <div class="cover-wrap">
              <el-image v-if="coverUrl(note)" :src="coverUrl(note)" fit="cover" class="cover" referrerpolicy="no-referrer" />
              <div v-else class="cover cover--empty">无图</div>
              <el-tag size="small" class="kind-tag" :type="isVideo(note) ? 'danger' : 'primary'">{{ isVideo(note) ? '视频' : '图文' }}</el-tag>
            </div>
            <div class="note-title">{{ note.title || '未命名笔记' }}</div>
            <div class="note-meta">{{ note.author_name || '未知作者' }}</div>
            <div class="note-stats">
              <span>赞 {{ note.likes || 0 }}</span>
              <span>藏 {{ note.collects || 0 }}</span>
              <span>评 {{ note.comments || 0 }}</span>
            </div>
            <div class="note-actions" @click.stop>
              <el-button
                size="small"
                :type="savedIds.has(note.note_id) ? 'info' : 'primary'"
                :loading="savingIds.has(note.note_id)"
                :disabled="savedIds.has(note.note_id)"
                @click="saveNote(note)"
              >{{ savedIds.has(note.note_id) ? '已保存' : '保存' }}</el-button>
              <el-button size="small" :loading="loadingCommentIds.has(note.note_id)" @click="toggleComments(note)">
                {{ commentMap[note.note_id] ? '收起' : '评论' }}
              </el-button>
              <el-link v-if="noteUrlOf(note)" :href="noteUrlOf(note)" target="_blank" type="primary" @click.stop>原文</el-link>
            </div>
            <div v-if="commentMap[note.note_id]" class="comment-preview" @click.stop>
              <div v-if="!commentMap[note.note_id].length" class="text-muted">暂无评论</div>
              <div v-for="c in commentMap[note.note_id].filter(x => !x.parent_comment_id).slice(0, 4)" :key="c.comment_id" class="comment-line">
                <strong>{{ c.user_name }}</strong> {{ c.content }}
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
      <div v-if="notes.length" class="load-more">
        <el-button :loading="loadingMore" :disabled="!hasMore" @click="loadMore">{{ hasMore ? '加载更多' : '没有更多了' }}</el-button>
      </div>
    </el-card>

    <el-drawer
      v-model="drawerVisible"
      :title="selectedNote?.title || '笔记详情'"
      size="480px"
      class="platform-preview-drawer"
    >
      <PlatformContentViewer
        v-if="viewerItem"
        :item="viewerItem"
        :loading="loadingDetail"
      />
      <el-alert
        v-if="detailError"
        :title="detailError"
        type="warning"
        show-icon
        :closable="false"
        style="margin-top: 12px"
      />
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Refresh } from '@element-plus/icons-vue'
import axios from 'axios'
import { PUBLISH_V2 } from '../utils/appBase'
import { publishV2Api } from '../api'
import PlatformContentViewer from '../components/platform/PlatformContentViewer.vue'
import { normalizeContent } from '../components/platform/normalizeContent'

const V2 = PUBLISH_V2
const DISCOVERY_TIMEOUT = 20000

const sortOptions = [
  { value: 0, label: '综合排序' }, { value: 1, label: '最新' },
  { value: 2, label: '最多点赞' }, { value: 3, label: '最多评论' }, { value: 4, label: '最多收藏' },
]
const typeOptions = [{ value: 0, label: '不限类型' }, { value: 1, label: '视频笔记' }, { value: 2, label: '普通笔记' }]
const timeOptions = [{ value: 0, label: '不限时间' }, { value: 1, label: '一天内' }, { value: 2, label: '一周内' }, { value: 3, label: '半年内' }]
const rangeOptions = [{ value: 0, label: '不限范围' }, { value: 1, label: '已看过' }, { value: 2, label: '未看过' }, { value: 3, label: '已关注' }]

const accounts = ref([])
const accountId = ref('')
const keyword = ref('')
const noteUrl = ref('')
const filters = reactive({ sort_type_choice: 0, note_type: 0, note_time: 0, note_range: 0 })
const notes = ref([])
const page = ref(1)
const hasMore = ref(false)
const searchedKeyword = ref('')
const error = ref('')
const loadingAccounts = ref(false)
const searching = ref(false)
const loadingMore = ref(false)
const fetchingUrl = ref(false)
const savedIds = ref(new Set())
const savingIds = ref(new Set())
const commentMap = ref({})
const loadingCommentIds = ref(new Set())
const drawerVisible = ref(false)
const selectedNote = ref(null)
const loadingDetail = ref(false)
const detailError = ref('')

const pcAccounts = computed(() =>
  accounts.value.filter((a) => a.platform === 'xiaohongshu' && (a.xhs_sessions?.pc || a.has_cookie))
)

const viewerItem = computed(() => {
  if (!selectedNote.value) return null
  const n = selectedNote.value
  return normalizeContent(
    {
      ...n,
      platform: 'xiaohongshu',
      note_url: noteUrlOf(n),
      detail_warning: detailError.value || '',
    },
    { platform: 'xiaohongshu', proxyUrl: publishV2Api.coverProxyUrl },
  )
})

const coverUrl = (n) => n.cover_url || n.image_urls?.[0] || ''
const noteUrlOf = (n) => n.note_url || (n.note_id ? `https://www.xiaohongshu.com/explore/${n.note_id}` : '')
const isVideo = (n) => `${n.type || ''}`.toLowerCase().includes('video') || Boolean(n.video_url)

const loadAccounts = async () => {
  loadingAccounts.value = true
  try {
    const res = await axios.get(`${V2}/accounts`)
    accounts.value = res.data.items || []
    if (!accountId.value && pcAccounts.value.length) {
      accountId.value = pcAccounts.value[0].account_id
    }
  } finally {
    loadingAccounts.value = false
  }
}

const loadSavedIds = async () => {
  try {
    const res = await axios.get(`${V2}/xiaohongshu/discovery/saved-note-ids`)
    savedIds.value = new Set(res.data.items || [])
  } catch { /* ignore */ }
}

const runSearch = async (nextPage, append) => {
  if (!accountId.value) {
    error.value = '请先选择 PC 账号'
    return
  }
  if (!keyword.value.trim()) {
    error.value = '请输入关键词'
    return
  }
  error.value = ''
  append ? (loadingMore.value = true) : (searching.value = true)
  try {
    const res = await axios.post(`${V2}/xiaohongshu/discovery/search`, {
      account_id: accountId.value,
      keyword: keyword.value.trim(),
      page: nextPage,
      ...filters,
    })
    const items = res.data.items || []
    notes.value = append ? [...notes.value, ...items] : items
    page.value = res.data.page || nextPage
    hasMore.value = !!res.data.has_more
    searchedKeyword.value = keyword.value.trim()
    commentMap.value = append ? commentMap.value : {}
  } catch (e) {
    error.value = e.response?.data?.detail || '搜索失败'
  } finally {
    searching.value = false
    loadingMore.value = false
  }
}

const loadMore = () => runSearch(page.value + 1, true)

const fetchByUrl = async () => {
  if (!accountId.value || !noteUrl.value.trim()) {
    error.value = '请选择账号并输入 URL'
    return
  }
  fetchingUrl.value = true
  error.value = ''
  try {
    const res = await axios.post(`${V2}/xiaohongshu/discovery/note-detail`, {
      account_id: accountId.value,
      url: noteUrl.value.trim(),
    })
    notes.value = [res.data]
    hasMore.value = false
    searchedKeyword.value = 'URL 直查'
    page.value = 1
  } catch (e) {
    error.value = e.response?.data?.detail || 'URL 直查失败'
  } finally {
    fetchingUrl.value = false
  }
}

const ensureDetail = async (note) => {
  const url = noteUrlOf(note)
  if (!url) return note
  const res = await axios.post(`${V2}/xiaohongshu/discovery/note-detail`, {
    account_id: accountId.value,
    url,
  })
  return { ...note, ...res.data, note_url: res.data.note_url || url }
}

const saveNote = async (note) => {
  if (!note?.note_id) return
  savingIds.value = new Set([...savingIds.value, note.note_id])
  try {
    // 直接用搜索结果落库，避免保存前再请求笔记详情（易因小红书 API 超时卡住）
    const payload = { ...note, note_url: noteUrlOf(note) }
    const preloaded = commentMap.value[note.note_id]
    const res = await axios.post(
      `${V2}/xiaohongshu/discovery/save`,
      {
        note: payload,
        account_id: accountId.value,
        source_keyword: searchedKeyword.value,
        fetch_comments: false,
        comments: preloaded || undefined,
      },
      { timeout: 20000 },
    )
    savedIds.value = new Set([...savedIds.value, note.note_id])
    ElMessage.success(res.data.duplicate ? '已在发现笔记库' : '已保存到发现笔记库')
  } catch (e) {
    const msg = e.code === 'ECONNABORTED'
      ? '保存超时，请检查网络或稍后重试'
      : (e.response?.data?.detail || e.message || '保存失败')
    ElMessage.error(msg)
  } finally {
    const s = new Set(savingIds.value)
    s.delete(note.note_id)
    savingIds.value = s
  }
}

const toggleComments = async (note) => {
  if (commentMap.value[note.note_id]) {
    const m = { ...commentMap.value }
    delete m[note.note_id]
    commentMap.value = m
    return
  }
  const url = noteUrlOf(note)
  if (!url) return
  loadingCommentIds.value = new Set([...loadingCommentIds.value, note.note_id])
  try {
    const res = await axios.post(`${V2}/xiaohongshu/discovery/note-comments`, {
      account_id: accountId.value,
      url,
    })
    commentMap.value = { ...commentMap.value, [note.note_id]: res.data.items || [] }
  } catch {
    ElMessage.error('评论加载失败')
  } finally {
    const s = new Set(loadingCommentIds.value)
    s.delete(note.note_id)
    loadingCommentIds.value = s
  }
}

const openDetail = async (note) => {
  selectedNote.value = note
  drawerVisible.value = true
  detailError.value = ''
  loadingDetail.value = true
  try {
    selectedNote.value = await ensureDetail(note)
  } catch {
    detailError.value = '详情加载失败，已保留列表预览'
  } finally {
    loadingDetail.value = false
  }
}

onMounted(async () => {
  await loadAccounts()
  await loadSavedIds()
})
</script>

<style scoped>
.xhs-discovery { padding: 4px 0 24px; }
.page-head { display: flex; justify-content: flex-end; align-items: flex-start; margin-bottom: 16px; }
.page-title { margin: 0 0 4px; font-size: 18px; }
.page-desc { margin: 0; color: #909399; font-size: 13px; }
.filter-card, .result-card { margin-bottom: 16px; }
.field-label { font-size: 12px; color: #909399; margin-bottom: 4px; }
.note-card { margin-bottom: 8px; cursor: pointer; }
.cover-wrap { position: relative; margin: -12px -12px 8px; }
.cover { width: 100%; aspect-ratio: 1; display: block; }
.cover--empty { display: flex; align-items: center; justify-content: center; background: #f5f7fa; color: #c0c4cc; }
.kind-tag { position: absolute; top: 8px; left: 8px; }
.note-title { font-size: 13px; font-weight: 500; line-height: 1.4; margin-bottom: 4px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.note-meta { font-size: 12px; color: #909399; }
.note-stats { font-size: 12px; color: #606266; display: flex; gap: 10px; margin: 6px 0; }
.note-actions { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.comment-preview { margin-top: 8px; padding-top: 8px; border-top: 1px solid #ebeef5; font-size: 12px; }
.comment-line { margin-bottom: 4px; }
.load-more { text-align: center; margin-top: 16px; }
.text-muted { color: #c0c4cc; }
</style>
